<script setup lang="ts">
/**
 * The counts beside a sidebar entry: each is a run state (`ExecutionStatus`) or an alert badge (`ZhStatusPill`), with
 * what it means as its tooltip and aria-label. Nothing to show renders no wrapper at all.
 */
import { ZhStatusPill, ZhTooltip } from '@zerohuman/ui'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import type { RunStatusTone } from '@/lib/statusTone'

/**
 * One count beside an entry: a run state (`ExecutionStatus`) or an alert badge (`ZhStatusPill`), with what it means.
 * A run state is coloured by its status unless it carries a tone of its own: the nav paints every run
 * state neutral (N5, #1745).
 */
export type NavCount = { key: string; count: number; label: string } & (
    | { status: 'blocked' | 'waiting_on_gate' | 'running' | 'queued' | 'waiting'; tone?: RunStatusTone }
    | { status?: undefined; tone: RunStatusTone }
)

defineProps<{ counts: NavCount[] }>()
</script>

<template>
    <div v-if="counts.length > 0" class="ml-auto flex shrink-0 items-center gap-1">
        <ZhTooltip v-for="count in counts" :key="count.key" :text="count.label">
            <ExecutionStatus v-if="count.status" :status="count.status" :count="count.count" :tone="count.tone" :aria-label="count.label" />
            <ZhStatusPill v-else :tone="count.tone" :count="count.count" :pulse="false" class="shrink-0" :data-nav-badge="count.tone" :aria-label="count.label" />
        </ZhTooltip>
    </div>
</template>
